///
/// EarthBrowser
///
/// Created by Matt Giger on 9/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Shared action cluster for anchored info panel headers: copy a share
/// link to the focused feature, save the current view, and close the
/// panel. The share link is built from the live ?target= URL param (the
/// canonical focused-entity signal), so this needs no per-entity props.
///
/// Every action button gets a styled tooltip (PanelActionTooltip, also
/// used by per-panel headerExtras buttons) instead of the native title
/// attribute, so labels are consistent and instant across all panels.
///

import { LAYERS } from "@/components/controls/layers-panel/layerRegistry"
import { useLayerVisibility } from "@/components/controls/layers-panel/useLayerVisibility"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useIsMobile } from "@/components/utils/use-mobile"
import { requestDismissAnchoredPanel } from "@/earthbrowser/design-system/AnchoredReactPanelBridge"
import { useShareLink } from "@/hooks/useShareLink"
import { useAppSelector, useAppStore } from "@/redux/store"
import type { InfoWindowContentType } from "@/redux/userInterfaceSlice"
import { featureEmbedSnippet } from "@/utils/embedSnippet"
import { cEmbedCodeEnabled } from "@/utils/featureGates"
import { focusedEntityCoords, savePlacemarkAtFocus } from "@/utils/placemarks"
import { saveCurrentView } from "@/utils/savedViews"
import { cn } from "@/utils/utils"
import { BookmarkPlus, Check, CodeXml, Link, MapPin, Table2 } from "lucide-react"
import { useState, type ReactNode } from "react"

// Info-window content types whose dataset key differs from the layer's
// visibility type (radar station panels belong to the NEXRAD sub-layer)
const cContentTypeToLayerKey: Partial<Record<InfoWindowContentType, string>> = {
	radar: "nexrad",
}

/** The Layers-panel entry whose table lists the focused entity's dataset, if any */
function layerForContentType(type: InfoWindowContentType | undefined) {
	if (!type) return undefined
	const key = cContentTypeToLayerKey[type] ?? type
	return LAYERS.find(layer => layer.commandId && layer.visibility === key)
}

const cButtonClass = "pointer-events-auto w-5 h-5 flex items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition-colors"

/**
 * Dark glass tooltip for anchored-panel chrome buttons. Wraps any button
 * (TooltipTrigger asChild) so per-panel headerExtras get the same look.
 */
export function PanelActionTooltip({ label, children }: { label: string; children: ReactNode }) {
	return (
		<TooltipProvider delayDuration={250}>
			<Tooltip>
				<TooltipTrigger asChild>{children}</TooltipTrigger>
				<TooltipContent
					side="bottom"
					sideOffset={6}
					className="z-[70] px-2 py-1 text-[11px]"
				>
					{label}
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	)
}

export function PanelHeaderActions({ className }: { className?: string }) {
	const [linkCopied, setLinkCopied] = useState(false)
	const [embedCopied, setEmbedCopied] = useState(false)
	const [placeSaved, setPlaceSaved] = useState(false)
	const contentData = useAppSelector(state => state.userInterface.infoWindow.contentData)
	const { shareFocusedFeature } = useShareLink()
	const store = useAppStore()
	const isMobile = useIsMobile()
	const { openLayerTable } = useLayerVisibility()
	const tableLayer = layerForContentType(contentData?.type)

	// Open the dataset's table in the data dock. On phones the table sheet
	// and this info sheet would stack on the bottom edge, so dismiss first
	const handleOpenTable = () => {
		if (!tableLayer) return
		if (isMobile) requestDismissAnchoredPanel()
		openLayerTable(tableLayer)
	}
	// Only entities with a usable ground location offer "Save this place"
	// (radar stations carry no coords; a focused placemark is already saved)
	const canSavePlace = focusedEntityCoords(contentData) !== null

	// Signed in: server-backed short link a recipient can open without an
	// account (required for placemarks); signed out: self-contained param URL
	const handleCopyLink = () => {
		void shareFocusedFeature().then(url => {
			if (!url) return
			return navigator.clipboard.writeText(url).then(() => {
				setLinkCopied(true)
				setTimeout(() => setLinkCopied(false), 1500)
			})
		})
	}

	const handleSavePlace = () => {
		const placemark = savePlacemarkAtFocus()
		if (!placemark) return
		setPlaceSaved(true)
		setTimeout(() => setPlaceSaved(false), 1500)
	}

	// Copy-paste embed snippet framing this feature: the ?target= param (the
	// canonical focused-entity signal) so the embed follows the feature's
	// live position, the camera pose as placeholder/fallback framing, and
	// the curated context layers for the feature type. Works signed out,
	// same as the saved-view embed buttons.
	const handleCopyEmbed = () => {
		if (!contentData) return
		const camera = store.getState().earthbrowser.cameraState
		const target = new URL(window.location.href).searchParams.get("target")
		const snippet = featureEmbedSnippet(camera, contentData.type, target)
		void navigator.clipboard.writeText(snippet).then(() => {
			setEmbedCopied(true)
			setTimeout(() => setEmbedCopied(false), 1500)
		})
	}

	return (
		<div className={cn("flex items-center gap-1", className)}>
			<PanelActionTooltip label={linkCopied ? "Link copied" : "Copy link to this feature"}>
				<button
					onClick={handleCopyLink}
					className={cn(cButtonClass, linkCopied && "text-emerald-400 hover:text-emerald-400")}
					aria-label="Copy link to this feature"
				>
					{linkCopied ? <Check className="w-3 h-3" /> : <Link className="w-3 h-3" />}
				</button>
			</PanelActionTooltip>
			{cEmbedCodeEnabled && contentData && (
				<PanelActionTooltip label={embedCopied ? "Embed code copied" : "Copy embed code"}>
					<button
						onClick={handleCopyEmbed}
						className={cn(cButtonClass, embedCopied && "text-emerald-400 hover:text-emerald-400")}
						aria-label="Copy embed code"
					>
						{embedCopied ? <Check className="w-3 h-3" /> : <CodeXml className="w-3 h-3" />}
					</button>
				</PanelActionTooltip>
			)}
			{canSavePlace && (
				<PanelActionTooltip label={placeSaved ? "Place saved" : "Save this place"}>
					<button
						onClick={handleSavePlace}
						className={cn(cButtonClass, placeSaved && "text-emerald-400 hover:text-emerald-400")}
						aria-label="Save this place"
					>
						{placeSaved ? <Check className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
					</button>
				</PanelActionTooltip>
			)}
			{tableLayer && (
				<PanelActionTooltip label={`Open ${tableLayer.name} table`}>
					<button
						onClick={handleOpenTable}
						className={cButtonClass}
						aria-label={`Open ${tableLayer.name} table`}
					>
						<Table2 className="w-3 h-3" />
					</button>
				</PanelActionTooltip>
			)}
			<PanelActionTooltip label="Save this view">
				<button
					onClick={() => { void saveCurrentView() }}
					className={cButtonClass}
					aria-label="Save this view"
				>
					<BookmarkPlus className="w-3 h-3" />
				</button>
			</PanelActionTooltip>
			<PanelActionTooltip label="Close">
				<button
					onClick={requestDismissAnchoredPanel}
					className={cButtonClass}
					aria-label="Close panel"
				>
					<svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
						<line x1="1" y1="1" x2="9" y2="9" />
						<line x1="9" y1="1" x2="1" y2="9" />
					</svg>
				</button>
			</PanelActionTooltip>
		</div>
	)
}
